import {
  ATTACHMENT_MAX_COUNT,
  attachmentDisplayClassifier,
} from "@/lib/AttachmentDisplayClassifier";

/**
 * Pending attachment state for agent inbox/ticket composers.
 */
export class ChatAttachmentComposerController {
  constructor() {
    /** @type {Array<{ id: string, file_name: string, mime_type?: string, file_size: number, localFile?: File, uploading?: boolean, error?: string }>} */
    this.pending = [];
    this.mode = "live_chat";
    this.ticketId = null;
  }

  /**
   * @param {'live_chat' | 'ticket'} mode
   * @param {string | null} ticketId
   */
  setContext(mode, ticketId = null) {
    this.mode = mode;
    this.ticketId = ticketId;
    this.clear();
  }

  /**
   * @param {File} file
   */
  validateFile(file) {
    if (this.pending.length >= ATTACHMENT_MAX_COUNT) {
      return { ok: false, error: `Maximum ${ATTACHMENT_MAX_COUNT} files per message.` };
    }
    return attachmentDisplayClassifier.validateFile(file);
  }

  addLocalFile(file) {
    const entry = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file_name: file.name,
      mime_type: file.type || undefined,
      file_size: file.size,
      localFile: file,
    };
    this.pending.push(entry);
    return entry;
  }

  removeById(id) {
    this.pending = this.pending.filter((p) => p.id !== id);
  }

  /**
   * @param {import("@/api/AttachmentApiClient").AttachmentApiClient} apiClient
   * @param {{ sessionId: string, workspaceId?: string | null }} liveParams
   */
  async uploadAllPending(apiClient, liveParams) {
    const ids = [];
    for (const item of this.pending) {
      if (item.id && !item.id.startsWith("local-") && !item.localFile) {
        ids.push(item.id);
        continue;
      }
      if (!item.localFile) continue;
      item.uploading = true;
      item.error = undefined;
      try {
        let meta;
        if (this.mode === "ticket" && this.ticketId) {
          meta = await apiClient.uploadTicket({
            ticketId: this.ticketId,
            file: item.localFile,
            workspaceId: liveParams.workspaceId,
          });
        } else {
          meta = await apiClient.uploadLiveChat({
            sessionId: liveParams.sessionId,
            file: item.localFile,
            workspaceId: liveParams.workspaceId,
          });
        }
        item.id = String(meta.id);
        item.file_name = meta.file_name || item.file_name;
        item.mime_type = meta.mime_type;
        item.file_size = meta.file_size ?? item.file_size;
        item.localFile = undefined;
        ids.push(item.id);
      } catch (e) {
        item.error = e instanceof Error ? e.message : "Upload failed";
        throw e;
      } finally {
        item.uploading = false;
      }
    }
    return ids;
  }

  /**
   * @param {string} text
   */
  canSend(text) {
    const hasText = Boolean((text || "").trim());
    const hasFiles =
      this.pending.some((p) => p.localFile) ||
      this.pending.some((p) => p.id && !p.id.startsWith("local-") && !p.uploading && !p.error);
    return hasText || hasFiles;
  }

  metadataForOptimistic() {
    return this.pending
      .filter((p) => p.id && !p.id.startsWith("local-") && !p.uploading && !p.error)
      .map((p) => ({
        id: p.id,
        file_name: p.file_name,
        mime_type: p.mime_type,
        file_size: p.file_size,
      }));
  }

  clear() {
    this.pending = [];
  }
}
